import { format, parseISO, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import type {
  FundMovement,
  FundMovementsPeriodSelection,
  FundMovementsResponse,
  FundMovementsSummary,
} from './types';
import type {
  MetaCarteirasFicFidcsChildrenResponse,
  MetaCarteirasFicFidcsChildTrade,
} from 'src/services/meta-carteiras-fic-fidcs';

export function formatCompetenceMonthLabel(competenceDate: string): string {
  try {
    const label = format(startOfMonth(parseISO(competenceDate)), 'MMMM/yyyy', {
      locale: ptBR,
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return competenceDate;
  }
}

export const ALL_PERIOD_SELECTION = 'all';

export function normalizeCompetenceDate(competenceDate: string): string {
  try {
    return format(startOfMonth(parseISO(competenceDate)), 'yyyy-MM-dd');
  } catch {
    return competenceDate;
  }
}

function isSameCompetenceMonth(
  movementDate: string,
  competenceDate: string,
): boolean {
  try {
    return (
      format(startOfMonth(parseISO(movementDate)), 'yyyy-MM') ===
      format(startOfMonth(parseISO(competenceDate)), 'yyyy-MM')
    );
  } catch {
    return false;
  }
}

function hasPositiveAmount(quantity: number | null, value: number | null) {
  return (quantity ?? 0) > 0 || (value ?? 0) > 0;
}

function buildSharePrice(quantity: number, value: number): number {
  if (quantity <= 0) return 0;
  return value / quantity;
}

function isEligibleForSummary(
  movement: FundMovement,
  parentOperationId: string,
): boolean {
  if (!movement.fundOperationId || !movement.fundSlug) return false;
  if (movement.fundOperationId === parentOperationId) return false;
  return true;
}

function mapTradeToMovements(
  trade: MetaCarteirasFicFidcsChildTrade,
): FundMovement[] {
  const movements: FundMovement[] = [];
  const identity = trade.operationId ?? trade.cnpj ?? trade.name;

  if (hasPositiveAmount(trade.qtAquisNegoc, trade.vlAquisNegoc)) {
    const shareQuantity = trade.qtAquisNegoc ?? 0;
    const totalValue = trade.vlAquisNegoc ?? 0;
    movements.push({
      id: `${identity}-compra-${trade.dtComptc}`,
      date: trade.dtComptc,
      type: 'compra',
      fundName: trade.name,
      fundSlug: trade.slug,
      fundOperationId: trade.operationId,
      shareQuantity,
      totalValue,
      sharePrice: buildSharePrice(shareQuantity, totalValue),
      netValue: totalValue,
    });
  }

  if (hasPositiveAmount(trade.qtVendaNegoc, trade.vlVendaNegoc)) {
    const shareQuantity = trade.qtVendaNegoc ?? 0;
    const totalValue = trade.vlVendaNegoc ?? 0;
    movements.push({
      id: `${identity}-venda-${trade.dtComptc}`,
      date: trade.dtComptc,
      type: 'venda',
      fundName: trade.name,
      fundSlug: trade.slug,
      fundOperationId: trade.operationId,
      shareQuantity,
      totalValue,
      sharePrice: buildSharePrice(shareQuantity, totalValue),
      netValue: -totalValue,
    });
  }

  return movements;
}

function buildSummary(movements: FundMovement[]): FundMovementsSummary {
  const purchases = movements.filter((item) => item.type === 'compra');
  const sales = movements.filter((item) => item.type === 'venda');

  const purchasesValue = purchases.reduce(
    (acc, item) => acc + item.totalValue,
    0,
  );
  const purchasesQuantity = purchases.reduce(
    (acc, item) => acc + item.shareQuantity,
    0,
  );
  const salesValue = sales.reduce((acc, item) => acc + item.totalValue, 0);
  const salesQuantity = sales.reduce(
    (acc, item) => acc + item.shareQuantity,
    0,
  );

  return {
    purchases: {
      totalValue: purchasesValue,
      shareQuantity: purchasesQuantity,
    },
    sales: {
      totalValue: salesValue,
      shareQuantity: salesQuantity,
    },
    net: {
      totalValue: purchasesValue - salesValue,
      shareQuantity: purchasesQuantity - salesQuantity,
    },
  };
}

function buildEligibleSummary(
  movements: FundMovement[],
  periodSelection: FundMovementsPeriodSelection,
  parentOperationId: string,
): FundMovementsSummary {
  return buildSummary(
    movements.filter(
      (movement) =>
        (periodSelection === ALL_PERIOD_SELECTION ||
          isSameCompetenceMonth(movement.date, periodSelection)) &&
        isEligibleForSummary(movement, parentOperationId),
    ),
  );
}

export function getAvailableCompetencesFromMovements(
  movements: FundMovement[],
): string[] {
  return [
    ...new Set(
      movements
        .map((movement) => normalizeCompetenceDate(movement.date))
        .filter((competence) => /^\d{4}-\d{2}-\d{2}$/.test(competence)),
    ),
  ].sort((a, b) => b.localeCompare(a));
}

export function getAvailableMovementCompetences(
  response: MetaCarteirasFicFidcsChildrenResponse,
): string[] {
  return getAvailableCompetencesFromMovements(
    (response.trades?.data ?? []).flatMap(mapTradeToMovements),
  );
}

/**
 * Cards: agrega o mês selecionado, excluindo o FIC pai e fundos não cadastrados.
 * Tabela: apenas o mês de competência selecionado (histórico do mês),
 * sem filtro de elegibilidade.
 */
export function mapChildrenToFundMovements(
  operationId: string,
  periodSelection: FundMovementsPeriodSelection,
  response: MetaCarteirasFicFidcsChildrenResponse,
): FundMovementsResponse {
  const allMovements = (response.trades?.data ?? [])
    .flatMap(mapTradeToMovements)
    .sort((a, b) => b.date.localeCompare(a.date));

  const movements =
    periodSelection === ALL_PERIOD_SELECTION
      ? allMovements
      : allMovements.filter((movement) =>
          isSameCompetenceMonth(movement.date, periodSelection),
        );

  const isAllPeriod = periodSelection === ALL_PERIOD_SELECTION;

  return {
    operationId,
    referenceMonth: isAllPeriod
      ? ALL_PERIOD_SELECTION
      : format(startOfMonth(parseISO(periodSelection)), 'yyyy-MM'),
    referenceMonthLabel: isAllPeriod
      ? 'Todo período'
      : formatCompetenceMonthLabel(periodSelection),
    summary: buildEligibleSummary(allMovements, periodSelection, operationId),
    movements,
  };
}
